import type { ColDef } from 'ag-grid-community';
import AsyncCheckboxFilter, { type AsyncCheckboxFilterParams } from './AsyncCheckboxFilter';
import type { ExtendedColDef } from '../../types/grid';

export type TypedColumnFilter = 'agTextColumnFilter' | 'agNumberColumnFilter' | 'agDateColumnFilter';

export function checkboxFilter(
  filterParams: AsyncCheckboxFilterParams = {},
): Pick<ExtendedColDef, 'filter' | 'filterParams'> {
  return {
    filter: AsyncCheckboxFilter as any,
    filterParams,
  };
}

export function typedFilterWithCheckboxes(
  typedFilter: TypedColumnFilter,
  typedFilterParams: Record<string, unknown> = {},
  checkboxFilterParams: AsyncCheckboxFilterParams = {},
): Pick<ExtendedColDef, 'filter' | 'filterParams'> {
  return {
    filter: 'agMultiColumnFilter',
    filterParams: {
      filters: [
        {
          filter: typedFilter,
          display: 'subMenu',
          title: typedFilter === 'agDateColumnFilter'
            ? 'Date filter'
            : typedFilter === 'agNumberColumnFilter'
              ? 'Number filter'
              : 'Text filter',
          filterParams: typedFilterParams,
        },
        {
          filter: AsyncCheckboxFilter,
          display: 'subMenu',
          title: 'Choose values',
          filterParams: checkboxFilterParams,
        },
      ],
    },
  };
}

function inferredTypedFilter<T>(column: ColDef<T>): TypedColumnFilter {
  if (column.filter === 'agNumberColumnFilter' || column.filter === 'agDateColumnFilter') {
    return column.filter;
  }
  if (column.filter === 'agTextColumnFilter') return column.filter;

  const types = Array.isArray(column.type) ? column.type : [column.type];
  if (column.cellDataType === 'number' || types.includes('rightAligned')) {
    return 'agNumberColumnFilter';
  }
  if (column.cellDataType === 'date' || column.cellDataType === 'dateString') {
    return 'agDateColumnFilter';
  }
  return 'agTextColumnFilter';
}

/** Adds checkbox choices to custom/client-side grids outside TableGrid/ReportGrid. */
export function withClientSideCheckboxFilters<T>(columns: ColDef<T>[]): ColDef<T>[] {
  return columns.map((column) => {
    if (column.filter === false || column.filter === AsyncCheckboxFilter) return column;
    if (column.filter === 'agMultiColumnFilter') return column;
    if (typeof column.filter === 'function') return column;

    if (column.filter === 'agSetColumnFilter') {
      return { ...column, ...checkboxFilter() };
    }

    const typedFilter = inferredTypedFilter(column);
    const typedParams = column.filterParams && typeof column.filterParams === 'object'
      ? column.filterParams as Record<string, unknown>
      : {};
    return {
      ...column,
      ...typedFilterWithCheckboxes(typedFilter, typedParams),
    };
  });
}
